// src/pages/About.js
import { useRef, useState } from 'react';
import FadeIn from '../components/FadeIn';

const TEAM = [
  {
    id: 1,
    name: 'Ivy Stuart',
    role: 'Co-Founder & Lead Developer',
    photo: null,
    blurb:
      'Driven by a mission to provide independent creators and local entrepreneurs with a high-end technical edge, I specialize in building unique digital experiences through my own proprietary frameworks. Since earning my four certifications in web and mobile development in 2023, I have transitioned into full-time freelance work, helping many artists and small businesses transform their online presence into functional tools that drive real engagement. I prioritize "jargon-free" collaboration and transparent communication, ensuring that you don\'t need to be a tech expert to have a professional, high-performance website. My commitment to community-focused work also extends beyond technology; I am currently in the final stages of launching a non-profit dedicated to supporting and empowering women who have experienced abuse. As I continue to expand my digital services into the mobile application space, I remain dedicated to the same handcrafted quality and advocacy that defines my professional and personal life. When I\'m not refining code or preparing for the non-profit\'s launch, you can usually find me in the garden, playing the ukulele, or working on my latest crochet project.',
  },
  {
    id: 2,
    name: 'James Curtis',
    role: 'Co-Founder & Systems & Security Engineer',
    photo: '/james.png',
    blurb:
      'With over a decade of experience building and securing enterprise-scale technology environments, I bring a level of technical rigor to our work that most small business and creator-focused agencies don\'t have access to. My career has taken me from hands-on IT support all the way through security engineering, site reliability, and cybersecurity management, giving me a well-rounded perspective on how systems are built, how they break, and how to protect them at every layer.\n\nMy focus here is on the back end, the infrastructure, and the security considerations that ensure every site we deliver is trustworthy and resilient. That means evaluating how data flows, security hardening, and administrative functions. We make sure every site we deliver meets a professional standard of reliability and protection, the kind of attention that independent creators and small businesses rarely get access to.\n\nWhen I step away from the terminal, you\'ll usually find me tinkering with a 3D printer, automating something around the house, or deep in a side project that probably didn\'t need to exist, but does now.',
  },
];

function About() {
  const teamRef = useRef(null);
  const [activeMember, setActiveMember] = useState(0);
  const [expandedIds, setExpandedIds] = useState(new Set());

  function toggleBio(id) {
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function handleTeamScroll() {
    const el = teamRef.current;
    if (!el) return;
    setActiveMember(Math.round(el.scrollLeft / el.offsetWidth));
  }

  function scrollToMember(index) {
    const el = teamRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.offsetWidth, behavior: 'smooth' });
    setActiveMember(index);
  }

  return (
    <div className="about-page">

      {/* ── Hero ── */}
      <section className="about-hero">
        <FadeIn>
          <p className="offer-heading">Who We Are</p>
          <h1 className="services-hero-title">About Us</h1>
        </FadeIn>
      </section>

      {/* ── About the Company ── */}
      <section className="about-company-section">
        <FadeIn>
          <div className="about-company-inner">
            <div className="about-company-text">
              <p className="offer-heading">Our Story</p>
              <h2 className="about-section-title">About the Company</h2>
              <p className="about-body">
                CS Devs (Curtis Stuart Developers) was born from a shared obsession with technical
                problem-solving and a belief that high-end digital infrastructure should be accessible
                to everyone—from local entrepreneurs to independent creators. What started as a
                partnership built on late-night Raspberry Pi projects and custom server builds has
                evolved into a full-service digital agency that bridges the gap between creative
                vision and enterprise-grade reliability.
              </p>
              <p className="about-body">
                At CS Devs, we don't believe in one-size-fits-all solutions. We offer a range of
                development tiers to meet you where you are:
              </p>
              <p className="about-body">
                <strong>Artisanal Templates:</strong> For those seeking a professional edge with a
                streamlined launch, we utilize our own proprietary, "homemade" frameworks designed
                for speed and engagement.
              </p>
              <p className="about-body">
                <strong>Fully Customizable Experiences:</strong> For businesses with unique
                requirements, we build bespoke digital tools from the ground up, ensuring your
                online presence is as distinct as your brand.
              </p>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ── Meet the Team ── */}
      <section className="team-section">
        <FadeIn>
          <p className="offer-heading">The People Behind the Work</p>
          <h2 className="about-section-title about-section-title--center">Meet the Team</h2>
        </FadeIn>

        <div className="team-grid" ref={teamRef} onScroll={handleTeamScroll}>
          {TEAM.map((member, i) => (
            <FadeIn key={member.id} delay={i * 150}>
              <div className="team-card">
                <div className="team-photo-wrapper">
                  {member.photo
                    ? <img src={member.photo} alt={member.name} className="team-photo" />
                    : <div className="team-photo-placeholder"><span>{member.name.charAt(0)}</span></div>
                  }
                </div>
                <div className="team-card-body">
                  <h3 className="team-name">{member.name}</h3>
                  <span className="team-role">{member.role}</span>
                  <div className={`team-blurb-text${expandedIds.has(member.id) ? ' team-blurb-text--expanded' : ''}`}>
                    {member.blurb.split('\n\n').map((para, idx) => (
                      <p key={idx} className="team-blurb">{para}</p>
                    ))}
                  </div>
                  <button className="team-read-more" onClick={() => toggleBio(member.id)}>
                    {expandedIds.has(member.id) ? 'Read less' : 'Read more'}
                  </button>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
        <div className="carousel-dots">
          {TEAM.map((_, i) => (
            <button
              key={i}
              className={`carousel-dot${activeMember === i ? ' carousel-dot--active' : ''}`}
              onClick={() => scrollToMember(i)}
              aria-label={`Go to team member ${i + 1}`}
            />
          ))}
        </div>
      </section>

    </div>
  );
}

export default About;
